import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Mail, AlertCircle } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import MobileFooter from './MobileFooter';
import BaseUrl from '../Baseurl/baseurl';
import { Card, Badge, Avatar, Button, Loader, EmptyState, Eyebrow, Heading } from '../ui';

const NotificationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        const memberData = JSON.parse(localStorage.getItem('memberData'));

        if (!token || !memberData?.mid) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`${BaseUrl}/api/notifications/${memberData.mid}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to fetch notifications');
        }

        if (data.success !== false) {
          const transformedNotifications = data.data.map(notif => ({
            ...notif,
            unread: !notif.is_read,
            sender_name: notif.sender ? `${notif.sender.first_name} ${notif.sender.last_name}` : 'Unknown',
            created_at: notif.createdAt,
            note: notif.note || null
          }));
          setNotifications(transformedNotifications);
        } else {
          throw new Error(data.message || 'Failed to fetch notifications');
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const memberData = JSON.parse(localStorage.getItem('memberData'));

      if (!token || !memberData?.mid) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${BaseUrl}/api/mark-read/${notificationId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to mark notification as read');
      }

      if (data.success !== false) {
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === notificationId ? { ...notif, unread: false, is_read: true } : notif
          )
        );
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError(err.message);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const memberData = JSON.parse(localStorage.getItem('memberData'));

      if (!token || !memberData?.mid) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${BaseUrl}/api/mark-all-read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to mark all notifications as read');
      }

      if (data.success !== false) {
        setNotifications(prev =>
          prev.map(notif => ({ ...notif, unread: false, is_read: true }))
        );
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError(err.message);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return t('justNow') || 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} ${t('minutesAgo') || 'minutes ago'}`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} ${t('hoursAgo') || 'hours ago'}`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} ${t('daysAgo') || 'days ago'}`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg">
        <Header />
        <Loader label={t('loading') || 'Loading notifications…'} />
        <Footer />
        <MobileFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <Header />

      {/* Page Header Banner */}
      <div className="on-dark bg-navy">
        <div className="max-w-7xl mx-auto px-4 py-7 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full text-text ring-1 ring-inset ring-line/15 transition-colors hover:bg-primary/10 hover:text-ink"
            aria-label={t('back') || 'Back'}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <Eyebrow>{t('inbox') || 'Inbox'}</Eyebrow>
            <Heading variant="display" className="mt-3">
              {t('notifications') || 'Notifications'} <em className="kbc-serif">updates</em>
            </Heading>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">


        {/* Error */}
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-danger/20 bg-danger/10 px-4 py-3 text-sm font-medium text-danger">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Mark all read */}
        {notifications.length > 0 && notifications.some(n => n.unread) && (
          <div className="flex justify-end mb-4">
            <Button
              onClick={handleMarkAllRead}
              size="sm"
              leftIcon={<CheckCircle className="w-4 h-4" />}
            >
              {t('markAllRead') || 'Mark all as read'}
            </Button>
          </div>
        )}

        {/* Notifications list */}
        {notifications.length === 0 ? (
          <EmptyState
            icon={<Mail className="w-7 h-7" />}
            title={t('No Notifications') || 'No Notifications'}
            description={t('noNotificationsDesc') || "You're all caught up. New updates will appear here."}
          />
        ) : (
          <div className="space-y-3">
            {notifications.map((notif) => {
              const unread = Boolean(notif.unread);
              return (
                <Card
                  key={notif.id}
                  padding="sm"
                  interactive={unread}
                  onClick={() => unread && handleMarkAsRead(notif.id)}
                  className={`relative transition ${unread ? 'cursor-pointer ring-1 ring-inset ring-primary/25' : 'cursor-default'}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="relative">
                      <Avatar name={notif.sender_name} size="md" />
                      {unread && (
                        <span className="absolute -bottom-0.5 -right-0.5 inline-block w-3 h-3 bg-danger rounded-full ring-2 ring-surface"></span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${unread ? 'font-semibold text-ink' : 'text-text'}`}>{notif.message}</p>
                        <span className="shrink-0 text-xs text-muted">{formatTimeAgo(notif.created_at)}</span>
                      </div>
                      {notif.note && (
                        <p className="mt-1 text-sm text-muted break-words">{notif.note}</p>
                      )}
                      {unread && (
                        <Badge tone="primary" className="mt-2">
                          {t('new') || 'NEW'}
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
      <MobileFooter />
    </div>
  );
};

export default NotificationsPage;